import { useEffect, useRef } from "react";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import axios from "axios";
import { useAppSelector } from "redux/hooks";
import { isLoggedIn } from "redux/reducers/userSlice";

import { defaultLayout, landingLayout } from "./layout/default";
import {
  getFeaturedClassrooms,
  getClassroom,
} from "lib/common/endpoints/public";
import { createClassroomSlug } from "utils/slugify";
import { imgUrl, formatNpr } from "utils/constants";
import Image from "next/image";
import HeroSection, { LANDING_IMAGES } from "components/HeroSection";
import SEO from "lib/components/SEO";
import {
  fetchPromotion,
  Promotion,
} from "lib/components/Promotion/usePromotion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
  organizationLd,
  websiteLd,
  courseLd,
  faqLd,
  breadcrumbLd,
  localBusinessLd,
  itemListLd,
  softwareApplicationLd,
} from "lib/seo/jsonLd";

// All below-fold sections are code-split so their JS doesn't block the
// critical path. ssr:true (default) keeps them in the SSR HTML (no CLS),
// ssr:false for UI-only widgets that have no SEO value.
const About = dynamic(() => import("./IndexAbout"));
const StatsStrip = dynamic(() => import("lib/components/FeaturesStrip"));
const TestimonialsTimeline = dynamic(
  () => import("lib/components/TestimonialsTimeline"),
);
const ClamphookFeatures = dynamic(
  () => import("lib/components/Landing/ClamphookFeatures"),
);
const HomeFAQ = dynamic(() => import("lib/components/Landing/HomeFAQ"));
const MobileCard = dynamic(() => import("lib/components/MobileCard"));
const PromotionPopup = dynamic(
  () => import("lib/components/Promotion/PromotionPopup"),
  { ssr: false },
);

// Define Course Type
interface Course {
  _id: { $oid: string } | string;
  sn?: number;
  name: string;
  thumbnail?: string | null;
  banner?: string | null;
  label?: string;
  initialPrice?: number | null;
  discountedPrice?: number | null;
}

const getCourseId = (course: Course) => {
  if (typeof course._id === "string") return course._id;
  return course._id?.$oid;
};

const toMediaUrl = (path?: string | null) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return imgUrl + (path.startsWith("/") ? path.slice(1) : path);
};

const getCourseImageUrl = (course: Course) =>
  toMediaUrl(course.banner) ??
  toMediaUrl(course.thumbnail) ??
  "/img/Clamphook.png";

// Static Props
export const getStaticProps: GetStaticProps<{
  goals: never[];
  stats: Record<string, never>;
  courses: Course[];
  promoPopup: { data: Promotion[] } | null;
  heroImageIndex: number;
}> = async () => {
  let courses: Course[] = [];

  try {
    const response = await axios.get(getFeaturedClassrooms);
    const result = response.data;
    if (result?.success && Array.isArray(result?.data)) {
      const featured = result.data as Course[];

      // Enrich each featured course with the full detail document so we get
      // banner/thumbnail for the card image (the trimmed /featured response
      // may not have these populated) while keeping initialPrice/discountedPrice
      // from the detail as the source of truth.
      courses = await Promise.all(
        featured.map(async (course) => {
          try {
            const id = getCourseId(course);
            if (!id) return course;
            const detail = (await axios.get(getClassroom + id)).data?.data;
            return {
              ...course,
              banner: detail?.banner ?? course.banner ?? null,
              thumbnail: detail?.thumbnail ?? course.thumbnail ?? null,
              initialPrice: detail?.initialPrice ?? course.initialPrice ?? null,
              discountedPrice:
                detail?.discountedPrice ?? course.discountedPrice ?? null,
            } as Course;
          } catch {
            return course;
          }
        }),
      );
    }
  } catch (error) {}

  // Prefetched at build/revalidate time so PromotionPopup's image doesn't
  // wait on a client-side fetch before it can even start loading.
  const promoPopup = await fetchPromotion("popup");

  // Deterministic on the server so SSR/SSG HTML and the client's hydrated
  // output always agree -- picking this with Math.random() in a client
  // useEffect (the old approach) meant the priority-preloaded hero image
  // from SSR always got swapped for a different, unpreloaded one right
  // after mount, forcing a second image fetch that tanked mobile LCP.
  const heroImageIndex = Math.floor(Date.now() / 60000) % LANDING_IMAGES.length;

  return {
    props: {
      goals: [],
      stats: {},
      courses,
      promoPopup,
      heroImageIndex,
    },
    revalidate: 60,
  };
};

// HomePage Component
function HomePage({
  goals,
  stats,
  courses,
  promoPopup,
  heroImageIndex,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const coursesScrollRef = useRef<HTMLDivElement>(null);
  const isLoggedIn_ = useAppSelector(isLoggedIn);
  const router = useRouter();

  const homeFaqs = [
    {
      question:
        "Which is the best IOE entrance preparation institute in Nepal?",
      answer:
        "Clamphook is Nepal's best IOE entrance preparation platform and top favourite among students. It is built by Pulchowk engineering graduates, offers weekly CBT mock tests identical to the real IOE exam, daily doubt sessions, and has helped over 100,000 students across Nepal.",
    },
    {
      question:
        "Is Clamphook the best IOE entrance preparation institute in Kathmandu?",
      answer:
        "Yes. Clamphook Academy runs daily live classes in Kathmandu and online for students based elsewhere in Nepal. Expert mentors who have cracked the IOE entrance themselves guide every batch.",
    },
    {
      question: "Does Clamphook offer BSc CSIT entrance preparation?",
      answer:
        "Yes, Clamphook offers comprehensive BSc CSIT entrance preparation including subject-wise live classes, notes, weekly mock tests, and CBT practice aligned with the Tribhuvan University CSIT entrance pattern.",
    },
    {
      question: "Can I prepare for IOE entrance online with Clamphook?",
      answer:
        "Yes. Clamphook offers live online classes, recorded sessions, 200+ topic-wise CBT tests, and a mobile app, so students from Pokhara, Birgunj, Itahari, and anywhere across Nepal can prepare for IOE and CSIT entrance from home.",
    },
    {
      question: "How does Clamphook's IOE mock test work?",
      answer:
        "Every Sunday, students sit a full 100-question, 140-mark CBT mock exam in an interface identical to the real IOE exam. By Monday morning they receive a percentile rank, subject-wise weak spot report, and an estimated all-Nepal IOE rank.",
    },
    {
      question: "How to prepare for IOE entrance exam in 2083?",
      answer:
        "Start with a structured course covering Physics, Chemistry, Mathematics, and English at the NEB +2 level. Focus the first three months on building concepts, then shift to timed mock tests and past-paper practice. Clamphook's IOE preparation course follows exactly this structure with weekly full-pattern CBTs, daily doubt sessions, and mentors who cracked IOE themselves.",
    },
    {
      question: "What is the IOE entrance exam pattern for 2083?",
      answer:
        "The IOE entrance exam is a 2-hour Computer-Based Test (CBT) with 100 multiple-choice questions worth 140 marks (60 one-mark and 40 two-mark questions) covering Physics, Chemistry, Mathematics, and English. Each wrong answer loses 10% of that question's marks. The exam tests speed, accuracy, and conceptual clarity at the NEB +2 level.",
    },
  ];

  const jsonLd = [
    organizationLd(),
    localBusinessLd(),
    websiteLd(),
    breadcrumbLd([{ name: "Home", url: "/" }]),
    itemListLd("Best IOE & CSIT Entrance Preparation Courses in Nepal", [
      {
        name: "IOE Entrance Preparation 2083 — Online & Physical",
        url: "/ioe-entrance-preparation",
        description:
          "The best IOE entrance preparation institute in Nepal offering full-syllabus preparation with weekly CBT mock tests identical to the real exam. Join our expert mentors from Pulchowk for daily doubt sessions online nationwide and physical in Kathmandu.",
        image: "https://clamphook.com/img/exams/ioe.png",
      },
      {
        name: "BSc CSIT Entrance Preparation 2083 — Online & Physical",
        url: "/csit-entrance-preparation",
        description:
          "Comprehensive BSc CSIT entrance preparation with live classes, recorded sessions, weekly CSIT-pattern mock tests, and CBT practice. Covers Physics, Chemistry, Mathematics, English, and computing aptitude at TU depth.",
        image: "https://clamphook.com/img/exams/csit.png",
      },
    ]),
    faqLd(homeFaqs),
    softwareApplicationLd(),
    ...courses
      .filter((c) => !!c.name)
      .map((course) =>
        courseLd({
          name: course.name,
          description:
            course.label ||
            `${course.name}. An IOE, CSIT, or NEB entrance preparation course by Clamphook Academy.`,
          url: `https://clamphook.com/classrooms/${createClassroomSlug(course.name, getCourseId(course) || "")}`,
          image: getCourseImageUrl(course),
          price: course.discountedPrice ?? course.initialPrice ?? undefined,
        }),
      ),
  ];

  return (
    <>
      <SEO
        title="Best IOE Entrance Preparation Institute in Kathmandu, Nepal & Online | Clamphook Academy"
        description="Best IOE entrance preparation institute in Kathmandu, Nepal& online offering full-syllabus preparation with weekly CBT mock tests identical to the real exam."
        canonicalUrl="https://clamphook.com/"
        ogImage="/img/og_image_main.png"
        ogType="website"
        keywords={[
          "best ioe entrance preparation institute",
          "IOE entrance preparation 2084",
          "online IOE preparation Nepal",
          "IOE mock test 2084",
          "Pulchowk entrance preparation",
          "how to prepare for IOE entrance",
          "IOE entrance syllabus 2084",
          "IOE CBT practice online",
          "best engineering entrance coaching Nepal",
          "IOE entrance exam pattern",
          "Clamphook Academy",
          "engineering entrance preparation Kathmandu",
          "pre-engineering entrance preparation",
          "best engineering institute kathmandu",
          "ioe entrance coaching",
          "be entrance classes nepal",
        ]}
        jsonLd={jsonLd}
      />
      <HeroSection courses={courses} heroImageIndex={heroImageIndex} />
      <PromotionPopup initialPromo={promoPopup} />

      {/* Courses Section */}
      <section className="pb-4 sm:px-6 lg:px-8 bg-gray-50">
        <StatsStrip />
        <div className="max-w-7xl mx-auto pt-16 px-4 sm:px-0">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-3">
            Enroll Today
          </p>
          <h2 className="font-extrabold text-center text-2xl md:text-4xl mb-8 md:mb-12">
            <span className="text-gray-900">Our </span>
            <span className="text-primary">Courses</span>
          </h2>

          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                coursesScrollRef.current?.scrollBy({
                  left: -330,
                  behavior: "smooth",
                })
              }
              className="hidden sm:flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 hover:shadow-lg transition-all"
              aria-label="Previous courses"
            >
              <CaretLeft size={18} className="text-gray-600" />
            </button>

            <div
              ref={coursesScrollRef}
              className="flex flex-1 gap-5 overflow-x-auto pb-2 scrollbar-hidden scroll-smooth snap-x snap-mandatory"
            >
              {courses.map((course, index) => (
                <div
                  key={getCourseId(course) || `${course.name}-${index}`}
                  className="bg-white rounded-xl border border-gray-200 flex-shrink-0 w-[16rem] sm:w-80 snap-start overflow-hidden group flex flex-col"
                >
                  {/* next/image proxies remote CDN URLs through Vercel's optimizer,
                      serving AVIF/WebP with the right srcset — avoids shipping
                      300+ KiB jpegs to mobile. */}
                  <div className="relative overflow-hidden aspect-video bg-primary-50">
                    <Image
                      src={getCourseImageUrl(course)}
                      alt={course.name}
                      fill
                      sizes="(max-width: 640px) 16rem, 20rem"
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                      quality={65}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  </div>
                  <div className="p-3 sm:p-6 flex flex-col flex-grow">
                    <h3 className="text-base sm:text-2xl font-bold text-gray-900 mb-2 line-clamp-3 min-h-[2.5rem] sm:min-h-[3.5rem]">
                      {course.name}
                    </h3>

                    {course.discountedPrice ? (
                      <div className="mb-4">
                        <span className="text-sm text-gray-500 line-through">
                          {course.initialPrice
                            ? formatNpr(course.initialPrice)
                            : ""}
                        </span>{" "}
                        <span className="text-sm sm:text-base font-bold text-success-800">
                          {formatNpr(course.discountedPrice)}
                        </span>
                      </div>
                    ) : course.initialPrice ? (
                      <p className="mb-4 text-sm sm:text-base font-bold text-gray-800">
                        {formatNpr(course.initialPrice)}
                      </p>
                    ) : (
                      <p className="mb-4 text-sm text-gray-600">
                        Fee: Refer notice
                      </p>
                    )}

                    <button
                      className="w-full bg-primary hover:bg-primary-600 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 mt-auto"
                      onClick={() =>
                        (window.location.href = `/classrooms/${createClassroomSlug(course.name, getCourseId(course) || "")}`)
                      }
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() =>
                coursesScrollRef.current?.scrollBy({
                  left: 330,
                  behavior: "smooth",
                })
              }
              className="hidden sm:flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 hover:shadow-lg transition-all"
              aria-label="Next courses"
            >
              <CaretRight size={18} className="text-gray-600" />
            </button>
          </div>
        </div>
      </section>
      {/*
        Section rhythm. Flat paper-white throughout, sections separated by a
        single hairline rule rather than alternating colour tints — the
        contrast comes from type scale and content, not background colour.
      */}
      <div className="bg-gray-50 border-t border-gray-200 py-14 md:py-16">
        <ClamphookFeatures />
      </div>
      <div className="bg-gray-50 border-t border-gray-200 py-14 md:py-16">
        <TestimonialsTimeline />
      </div>
      <div className="bg-gray-50 border-t border-gray-200 py-14 md:py-16">
        <About />
      </div>
      <div className="bg-gray-50 border-t border-gray-200 py-14 md:py-16">
        <MobileCard />
      </div>
      <div className="bg-gray-50 border-t border-gray-200">
        <HomeFAQ faqs={homeFaqs} />
      </div>
    </>
  );
}

HomePage.getLayout = landingLayout;
export default HomePage;
